import type { RadioCardProps as MRadioCardProps } from '@mantine/core'
import { Box, Group, Radio, Text } from '@mantine/core'
import type { ReactNode } from 'react'

import { theme } from '@/configs/theme'
import * as classes from '@/styles/RadioCard.css'

interface RadioCardProps extends MRadioCardProps {
  label: ReactNode
  description?: ReactNode
  /**
   * Show the description whether or not the card is selected. For a picker where the
   * descriptions are what the choice is made on, rather than detail about a choice
   * already made.
   */
  alwaysShowDescription?: boolean
}

export function RadioCard(props: RadioCardProps) {
  const { className, label, description, disabled, checked, alwaysShowDescription, ...radioCardProps } = props

  const body = (
    <Group wrap="nowrap" align="flex-start">
      <Radio.Indicator disabled={disabled} />

      <Box>
        <Text className={classes.label}>{label}</Text>
        {description && (
          <Text className={theme.cx(classes.description, alwaysShowDescription && classes.descriptionVisible)}>
            {description}
          </Text>
        )}
      </Box>
    </Group>
  )

  const cardClassName = theme.cx(classes.card, disabled && classes.cardDisabled, className)

  // Mantine's Radio.Card has no notion of disabled: its onClick calls the group's onChange
  // unconditionally, and arrow keys walk `[role="radio"][name=…]` calling `.click()`, which
  // `pointer-events: none` does not stop. So a disabled card is rendered outside the group
  // instead — announced as a disabled radio, skipped by arrow keys because it carries no
  // `name`, and inert to both mouse and keyboard.
  if (disabled) {
    return (
      <Box
        className={cardClassName}
        role="radio"
        aria-checked={checked ?? false}
        data-checked={checked || undefined}
        aria-disabled
        tabIndex={-1}>
        {body}
      </Box>
    )
  }

  return (
    <Radio.Card radius="md" checked={checked} {...radioCardProps} className={cardClassName}>
      {body}
    </Radio.Card>
  )
}
